import React, { useEffect, useState } from 'react';
import { Gallery, GalleryItem, ProjectPlaceholder, SocialShareBar } from '@core/ui';
import { Col, Row } from 'reactstrap';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { IconBrandFacebook, IconBrandInstagram, IconBrandYoutube } from '@tabler/icons-react';
import axios from '../../http/index.js';
import { sendVirtualPageView } from '../../shared/index.js';
import { useProduct } from '../../shared/productContext.jsx';
import { usePhases, useProjectPageConfig } from '../../stores/index.js';

/**
 * Detailseite eines Projekts.
 *
 * Gemeinsam ist alles, was beide Produkte gleich zeigen: Abruf von
 * `/projects/:id`, die Antwortkarten, die Galerie, der Social-Media-Block des
 * Teilnehmenden, das Teilen und das Grundlayout. Produktunterschiede kommen
 * ausschließlich aus dem Deskriptor:
 *
 * - `projectVotePanel`: der Abstimmbereich (Wettbewerb vs. Fördertopf). Der
 *   Zustandsautomat bleibt app-lokal, der Kern rendert nur die Komponente.
 * - `projectShare`: Dienste, optionaler Teilen-Text und optionaler Callback
 *   (Cassiopeia bucht darüber den Achievement-PATCH).
 * - `contestantAnswerFallbackHeader`: Standardüberschrift, wenn keine
 *   Antwort-Überschriften konfiguriert sind.
 * - `projectDetailComponents`: optionale Slots `Media` (Bild + Abzeichen,
 *   Cassiopeia) und `Banner` (Zahlen-und-Marke, Bigfoot). Fehlt ein Slot,
 *   rendert der Kern nichts.
 */
function Project(props) {
    const product = useProduct();
    const votingPhases = usePhases();
    const { contestantAnswerHeaders = [], shareImageUrl } = useProjectPageConfig();
    const [loadedPost, setLoadedPost] = useState(null);

    const { id } = useParams();

    useEffect(() => {
        if (!id) return;
        axios
            .get(`/projects/${id}`) // leading slash for route is necessary for archival detection!
            .then(({ data }) => setLoadedPost(data))
            .catch((error) => {
                console.error('Error fetching project:', error);
            });
        sendVirtualPageView(`/${id}`);
    }, [id]);

    const { Media, Banner } = product.projectDetailComponents || {};
    const VotePanel = product.projectVotePanel;

    let project = (
        <Row className="py-3">
            <Col xs="12">
                <ProjectPlaceholder />
            </Col>
        </Row>
    );

    if (loadedPost) {
        const activeAnswers = [];
        contestantAnswerHeaders.forEach((header, idx) => {
            const answerKey = `contestantAnswer${idx + 1}`;
            const answerValue = loadedPost[answerKey];
            if (header && header.trim() !== '' && answerValue && answerValue.trim() !== '') {
                activeAnswers.push({
                    header: header.trim(),
                    text: answerValue.trim(),
                });
            }
        });

        // Ohne konfigurierte Überschriften zeigt das Produkt die erste Antwort
        // unter seiner Standardüberschrift; der Text kommt aus dem Deskriptor.
        if (activeAnswers.length === 0 && product.contestantAnswerFallbackHeader && loadedPost.contestantAnswer1) {
            activeAnswers.push({
                header: product.contestantAnswerFallbackHeader,
                text: loadedPost.contestantAnswer1,
            });
        }

        const activeAnswersCount = activeAnswers.length;
        const shareText = product.projectShare?.shareText?.({ project: loadedPost, phases: votingPhases });

        project = (
            <>
                <Helmet>
                    <title>{`${loadedPost.contestantName} | ${process.env.REACT_APP_PROJECT_TITLE}`}</title>
                </Helmet>
                <Row className="my-3">
                    <Col xs="12" lg="5">
                        {Banner ? <Banner /> : null}
                        {Media ? <Media project={loadedPost} /> : null}
                        <Row className="mb-3">
                            <Col className="text-start">
                                <h3 className="mb-0">{loadedPost.contestantName}</h3>
                                <p className="my-0">{loadedPost.projectTitle}</p>
                            </Col>
                        </Row>
                        <Row className="mb-1">
                            {VotePanel ? <VotePanel project={loadedPost} phases={votingPhases} {...props} /> : null}
                        </Row>
                        <Row className="mb-3">
                            <Col xs="12" lg="12">
                                <SocialShareBar
                                    displayMode="dropdown"
                                    fullWidth
                                    services={product.projectShare?.services}
                                    shareTitle={loadedPost.contestantName}
                                    shareText={shareText}
                                    shareImageUrl={shareImageUrl}
                                    onShare={product.projectShare?.onShare}
                                />
                            </Col>
                        </Row>
                    </Col>

                    <Col xs="12" lg="7">
                        <Row>
                            {activeAnswers.map((answer, i) => (
                                <Col xs={{ size: 12, order: i + 2 }} lg={{ order: i + 1 }} key={i}>
                                    <h1 className="text-primary">{answer.header}</h1>
                                    <p>{answer.text}</p>
                                </Col>
                            ))}

                            {loadedPost.projectGallery?.length > 0 && (
                                <Col
                                    xs={{ size: 12, order: 1 }}
                                    lg={{ order: activeAnswersCount + 1 }}
                                    className="px-0"
                                >
                                    <Gallery>
                                        {loadedPost.projectGallery.map((galleryItem, index) => (
                                            <GalleryItem key={index} src={galleryItem.url} alt={galleryItem.alt} />
                                        ))}
                                    </Gallery>
                                </Col>
                            )}
                            <Col
                                xs={{ size: 12, order: activeAnswersCount + 2 }}
                                lg={{ order: activeAnswersCount + 2 }}
                            >
                                {loadedPost.contestantInstagramProfile ||
                                loadedPost.contestantFacebookProfile ||
                                loadedPost.contestantYouTubeChannel ? (
                                    <h3 className="text-primary">
                                        Jetzt dieses Profil auch auf Social Media erkunden:
                                    </h3>
                                ) : null}
                                <div className="d-flex flex-wrap">
                                    {loadedPost.contestantInstagramProfile ? (
                                        <a
                                            className="btn btn-light rounded-5 me-2 mb-2 d-flex align-items-center anl_project--instagram-profile-lnk"
                                            href={loadedPost.contestantInstagramProfile}
                                            target="_blank"
                                            rel="noopener nofollow"
                                        >
                                            <IconBrandInstagram
                                                className="me-2"
                                                style={{ color: '#C13584' }}
                                                size={20}
                                            />
                                            Instagram
                                        </a>
                                    ) : null}
                                    {loadedPost.contestantFacebookProfile ? (
                                        <a
                                            className="btn btn-light rounded-5 me-2 mb-2 d-flex align-items-center anl_project--facebook-profile-lnk"
                                            href={loadedPost.contestantFacebookProfile}
                                            target="_blank"
                                            rel="noopener nofollow"
                                        >
                                            <IconBrandFacebook
                                                className="me-2"
                                                style={{ color: '#1877F2' }}
                                                size={20}
                                            />
                                            Facebook
                                        </a>
                                    ) : null}
                                    {loadedPost.contestantYouTubeChannel ? (
                                        <a
                                            className="btn btn-light rounded-5 me-2 mb-2 d-flex align-items-center anl_project--youtube-profile-lnk"
                                            href={loadedPost.contestantYouTubeChannel}
                                            target="_blank"
                                            rel="noopener nofollow"
                                        >
                                            <IconBrandYoutube className="me-2" style={{ color: '#FF0000' }} size={20} />
                                            YouTube
                                        </a>
                                    ) : null}
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </>
        );
    }

    return <>{project}</>;
}

export default Project;
